import { useSelector } from "react-redux";

import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";

import { CellCheckBox } from "components/Table/CellFormatter/CellCheckBox";
import KsprButton from "components/UI/buttons/KsprButton";
import { getPriceOfferSelectedColumnById } from "slices/priceOffer/priceOfferSelectors";

import { useGetSomStatusFilterParams } from "./useGetSomStatusFilterParams";

const PriceOffersBaseTableColumnsSettings = () => {
	const userPermissions = useSelector((state) => state.auth.permissions);
	const selectedColumnById = useSelector(getPriceOfferSelectedColumnById);

	const { somStatusFilterCheckboxesData, checkedSomStatusFilterCount, somStatusFilterOpenPopoverIcon, somStatusFilterPopoverProps } =
		useGetSomStatusFilterParams();

	const clms = [
		{
			name: "check_box_select",
			title: "",
			width: "15px",
			cellHorAlign: "center",
			headHorAlign: "center",
			sort: false,
			filter: false,
			editable: false,
			cellJsx: ({ rowObj, clmName, funcs }) => {
				if (rowObj.som_item_id) {
					return (
						<td
							className="TableCell"
							clm={clmName}
							title="Использовано"
							onClick={() => {
								window.open(`/som_item/${rowObj.som_item_id}/`);
							}}
						>
							<TaskAltOutlinedIcon />
						</td>
					);
				} else if (rowObj.som_status === "excluded") {
					return (
						<td className="TableCell" clm={clmName} title="Исключена">
							<RemoveCircleOutlineOutlinedIcon />
						</td>
					);
				} else {
					return (
						<CellCheckBox
							rowObj={rowObj}
							clmName={clmName}
							setCheckedFunc={funcs?.setCheckedFunc}
							key={`${rowObj?.id}-checkbox-${clmName}`}
						/>
					);
				}
			},
			headerAction: "checkbox_multiple_options_filter",
			headerActionParams: {
				checkedCount: checkedSomStatusFilterCount,
				checkboxesData: somStatusFilterCheckboxesData,
				openPopoverIcon: somStatusFilterOpenPopoverIcon,
				popoverProps: somStatusFilterPopoverProps,
			},
		},
		{
			name: "ksr_code",
			title: "Код группы КСР",
			width: "140px",
			cellHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
			cellJsx: ({ rowObj }) => {
				const cellValue = rowObj.ksr_code == null ? "" : rowObj.ksr_code.toString();
				return (
					<td key={`cell-ksr_code-${rowObj.id}`} className="TableCell" clm="code" treecell="false" title={cellValue}>
						<div title={cellValue} className="TableCell" style={{ display: "flex", justifyContent: "space-between" }}>
							<span className="TableCell" style={{ marginLeft: "2px", flexBasic: "60%" }}>
								{cellValue}
							</span>
							<div>
								{rowObj.expert_valid === true ? <CheckCircleOutlinedIcon /> : <span style={{ paddingLeft: "17px" }}></span>}
								{rowObj.is_winner === true ? <EmojiEventsOutlinedIcon /> : <span style={{ paddingLeft: "17px" }}></span>}
							</div>
						</div>
					</td>
				);
			},
		},
		{
			name: "name",
			title: "Наименование строительного ресурса, затрат",
			width: "auto",
			cellHorAlign: "left",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
			advancedFilter: true,
		},
		{
			name: "unit",
			title: "Ед.изм.",
			width: "60px",
			cellHorAlign: "center",
			headHorAlign: "center",
			headHeight: "12px",
			marginLeft: "10px",
			sort: false,
			filter: false,
		},
		{
			name: "estimate_without_vat",
			title: "СЦ без НДС",
			width: "120px",
			cellHorAlign: "right",
			headHorAlign: "right",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
		{
			name: "company_name",
			title: "Поставщик",
			width: "190px",
			cellHorAlign: "left",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
			advancedFilter: true,
		},
		{
			name: "company_store_place",
			title: "Нас.пункт склада",
			width: "150px",
			cellHorAlign: "left",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: false,
			filter: false,
			advancedFilter: true,
		},
		{
			name: "pricezone_name",
			title: "Ценовая зона",
			width: "180px",
			cellHorAlign: "left",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
		{
			name: "offer_date",
			title: "Дата ЦП",
			width: "75px",
			cellHorAlign: "left",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
		{
			name: "kats_date_create",
			title: "Дата загрузки",
			width: "90px",
			cellHorAlign: "left",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
		{
			name: "price_offer_with_vat",
			title: "Цена с НДС в док-те",
			width: "90px",
			cellHorAlign: "right",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
		{
			name: "price_offer_without_vat",
			title: "Цена без НДС за ЕИ",
			width: "90px",
			cellHorAlign: "right",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
		{
			name: "price_transportation",
			title: "Стоимость перевозки",
			width: "100px",
			cellHorAlign: "right",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
		{
			name: "price_storage_percent",
			title: "ЗСР, %",
			width: "90px",
			cellHorAlign: "right",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
		{
			name: "price_storage_price",
			title: "ЗСР, руб.",
			width: "100px",
			cellHorAlign: "right",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
		// {
		// 	name: "price_level_print",
		// 	title: "Уровень цен",
		// 	width: "90px",
		// 	cellHorAlign: "right",
		// 	headHorAlign: "left",
		// 	headHeight: "12px",
		// 	marginLeft: "5px",
		// 	sort: false,
		// 	filter: false,
		// },
		{
			name: "company_inn",
			title: "ИНН",
			width: "90px",
			cellHorAlign: "right",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
		{
			name: "company_kpp",
			title: "КПП",
			width: "90px",
			cellHorAlign: "right",
			headHorAlign: "left",
			headHeight: "12px",
			marginLeft: "5px",
			sort: true,
			filter: false,
		},
	];

	return clms.filter(({ name }) => Boolean(selectedColumnById[name]) || (userPermissions.is_som_admin && name === "check_box_select"));
};

const PriceOffersDetailsPaper = ({ setInnKppInfoVisible, setInnKppInfoData }) => {
	return {
		top: [
			{
				row_key: "price_offer_with_vat",
				width: "14.5%",
				label: "Цена с НДС в док-те",
				component: "TextField",
				textAlign: "right",
			},
			{
				row_key: "price_offer_without_vat",
				width: "14.5%",
				label: "Цена без НДС за ЕИ",
				component: "TextField",
				textAlign: "right",
			},
			{
				row_key: "price_transportation",
				width: "14.5%",
				label: "Стоимость перевозки",
				component: "TextField",
				textAlign: "right",
			},
			{
				row_key: "price_storage_percent",
				width: "7%",
				label: "ЗСР, %",
				component: "TextField",
				textAlign: "right",
			},
			{
				row_key: "price_storage_price",
				width: "7%",
				label: "ЗСР, руб.",
				component: "TextField",
				textAlign: "right",
			},
			{
				row_key: "estimate_without_vat",
				width: "14.5%",
				label: "Сметная цена без НДС",
				component: "TextField",
				textAlign: "right",
			},
			{
				row_key: "price_level_print",
				width: "14.5%",
				label: "Уровень цен",
				component: "TextField",
				textAlign: "left",
			},
			{
				row_key: "company_status",
				width: "10%",
				label: "",
				component: "BoldSimpleLabel",
				textAlign: "left",
			},
		],
		bottom: [
			{
				row_key: "company_inn",
				width: "10%",
				label: "ИНН",
				component: "TextField",
				textAlign: "left",
			},
			{
				row_key: "company_kpp",
				width: "10%",
				label: "КПП",
				component: "TextField",
				textAlign: "left",
			},
			{
				row_key: "company_info",
				width: "8.5%",
				label: "",
				component: "СustomJsx",
				jsx: (rowObj) => {
					return (
						<KsprButton
							size="small"
							title="Инф. по ИНН"
							variant="outlined"
							label={<span style={{ position: "relative", top: "1px" }}>Инф. по ИНН</span>}
							onClick={() => {
								setInnKppInfoVisible(true);
								setInnKppInfoData({ inn: rowObj.company_inn, kpp: rowObj.company_kpp });
							}}
							// startIcon={<InfoOutlinedIcon rotate="180" />}
						></KsprButton>
					);
				},
			},
			{
				row_key: "company_store_place",
				width: "29.5%",
				label: "Населенный пункт склада, регион (ценовая зона)",
				component: "TextField",
				textAlign: "left",
			},
			{
				row_key: "company_name",
				width: "29.5%",
				label: "Наименование производителя (1) / поставщика (2)",
				component: "TextField",
				textAlign: "left",
			},
			{
				row_key: "company_site",
				width: "7%",
				label: "",
				component: "IconLink",
				icon: "PublicIcon",
				callback: (rowObj) => {
					if (rowObj.company_site) {
						if (rowObj.company_site.startsWith("http://") || rowObj.company_site.startsWith("https://")) {
							window.open(rowObj.company_site);
						} else {
							window.open("http://" + rowObj.company_site);
						}
					}
				},
				isActive: (rowObj) => {
					return String(rowObj.company_site != null && rowObj.company_site !== "");
				},
			},
		],
		rightBox: [
			{
				type: "List",
				label: "ОБОСНОВЫВАЮЩИЕ ДОКУМЕНТЫ",
				row_key: "link_files",
			},
			{
				type: "Elem",
				label: "ИСТОЧНИК ДАННЫХ",
				row_key: "project_name",
				rule: {
					key: "has_source_info",
					value: true,
				},
			},
		],
	};
};

export const priceOffersColumns = [
	{
		id: "ksr_code",
		name: "Код КСР",
	},
	{
		id: "name",
		name: "Наименование строительного ресурса, затрат",
	},
	{
		id: "unit",
		name: "Ед.изм.",
	},
	{
		id: "estimate_without_vat",
		name: "СЦ без НДС",
	},
	{
		id: "company_name",
		name: "Поставщик",
	},
	{
		id: "company_store_place",
		name: "Нас.пункт склада",
	},
	{
		id: "pricezone_name",
		name: "Ценовая зона",
	},
	{
		id: "offer_date",
		name: "Дата",
	},
	{
		id: "kats_date_create",
		name: "Дата загрузки",
	},
	{
		id: "price_offer_with_vat",
		name: "Цена с НДС в док-те",
	},
	{
		id: "price_offer_without_vat",
		name: "Цена без НДС за ЕИ",
	},
	{
		id: "price_transportation",
		name: "Стоимость перевозки",
	},
	{
		id: "price_storage_percent",
		name: "ЗСР, %",
	},
	{
		id: "price_storage_price",
		name: "ЗСР, руб.",
	},
	// {
	// 	id: "price_level_print",
	// 	name: "Уровень цен",
	// },
	{
		id: "company_inn",
		name: "ИНН",
	},
	{
		id: "company_kpp",
		name: "КПП",
	},
];

export { PriceOffersBaseTableColumnsSettings, PriceOffersDetailsPaper };
